import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useBooks } from '../hooks/useBooks'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Checkbox from '@mui/material/Checkbox'
import LoadingSpinner from '../components/LoadingSpinner'
import ErrorMessage from '../components/ErrorMessage'
import GenerationStatusBadge from '../components/GenerationStatusBadge'
import type { MasteryLevel } from '../types/models'
import { Button, Eyebrow, Plant, type Stage } from '../components/ui'

const STAGE: Record<MasteryLevel, Stage> = { SEED: 0, SPROUT: 1, SAPLING: 2, FOREST: 3 }
const STAGE_LABEL: Record<MasteryLevel, string> = { SEED: 'Seed', SPROUT: 'Sprout', SAPLING: 'Sapling', FOREST: 'Forest' }

export default function Library() {
  const { books, loading, error } = useBooks()
  const [selected, setSelected] = useState<Set<string>>(new Set())

  if (loading) return <LoadingSpinner />

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  // TODO(backend): wire bulk actions to real archive/export/delete endpoints.
  const bulk = (action: string) => {
    console.warn(`[bulk] ${action} on`, [...selected])
    setSelected(new Set())
  }

  const totalConcepts = books.reduce((n, b) => n + b.masteredQuestionsCount + b.learningQuestionsCount + b.newQuestionsCount, 0)

  return (
    <Box sx={{ maxWidth: 920, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 3 }}>
        <Box>
          <Eyebrow>{books.length} books · {totalConcepts} concepts</Eyebrow>
          <Typography sx={{ fontFamily: 'var(--font-serif)', fontWeight: 500, fontSize: 'clamp(26px,3.5vw,36px)', letterSpacing: '-0.6px', mt: 0.5 }}>
            Library
          </Typography>
        </Box>
        <Button to="/library/add" variant="primary">Add book</Button>
      </Box>

      {error && <ErrorMessage message={error} />}

      {selected.size > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 1.5,
            mb: 2,
            borderRadius: 'var(--r-md)',
            background: 'var(--moss-soft)',
            border: '1px solid var(--moss-line)',
          }}
        >
          <Typography sx={{ fontWeight: 600, color: 'var(--moss)' }}>{selected.size} selected</Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Button variant="ghost" size="sm" onClick={() => bulk('tag')}>Tag</Button>
          <Button variant="ghost" size="sm" onClick={() => bulk('archive')}>Archive</Button>
          <Button variant="ghost" size="sm" onClick={() => bulk('export')}>Export</Button>
          <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>Clear</Button>
        </Box>
      )}

      {books.length === 0 ? (
        <Box sx={{ ...cardSx, textAlign: 'center', py: 6 }}>
          <Typography sx={{ fontFamily: 'var(--font-serif)', fontSize: 24, mb: 1 }}>Plant your first book.</Typography>
          <Typography sx={{ color: 'var(--ink-2)', mb: 2 }}>Import a book and Recall extracts the concepts worth holding.</Typography>
          <Button to="/library/add" variant="primary">Add your first book</Button>
        </Box>
      ) : (
        // Keep minmax(0, 1fr): a bare auto column grows to the widest unbroken title and blows out narrow viewports.
        <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 1.5 }}>
          {books.map((book) => {
            const isSel = selected.has(book.id)
            return (
              <Box
                key={book.id}
                sx={{
                  ...cardSx,
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  p: 2,
                  cursor: 'pointer',
                  borderColor: isSel ? 'var(--moss-line)' : 'var(--border)',
                  background: isSel ? 'var(--moss-soft)' : 'var(--surface)',
                  '&:hover .row-check': { opacity: 1 },
                }}
              >
                <Box
                  className="row-check"
                  onClick={(e) => {
                    e.stopPropagation()
                    toggle(book.id)
                  }}
                  sx={{ position: 'relative', zIndex: 1, opacity: isSel ? 1 : 0, transition: 'opacity 120ms' }}
                >
                  <Checkbox
                    checked={isSel}
                    size="small"
                    sx={{ p: 0 }}
                    slotProps={{ input: { 'aria-label': `Select ${book.title}` } }}
                  />
                </Box>
                <Box role="img" aria-label={`Mastery: ${STAGE_LABEL[book.masteryLevel]}`} sx={{ display: 'flex' }}>
                  <Plant stage={STAGE[book.masteryLevel]} size={40} />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  {/* Stretched link: the whole row is clickable; the title stays a focusable <a>. */}
                  <Typography
                    component={Link}
                    to={`/library/${book.id}`}
                    noWrap
                    sx={{
                      display: 'block',
                      fontWeight: 600,
                      fontSize: 16,
                      color: 'inherit',
                      textDecoration: 'none',
                      '&::after': { content: '""', position: 'absolute', inset: 0 },
                    }}
                  >
                    {book.title}
                  </Typography>
                  <Typography sx={{ color: 'var(--ink-2)', fontSize: 14 }} noWrap>{book.author}</Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Eyebrow>
                      {book.masteredQuestionsCount} mastered · {book.learningQuestionsCount} learning · {book.newQuestionsCount} new
                    </Eyebrow>
                  </Box>
                </Box>
                <GenerationStatusBadge status={book.questionGenerationStatus} />
              </Box>
            )
          })}
        </Box>
      )}
    </Box>
  )
}

const cardSx = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--r-lg)',
  p: 3,
} as const
